import { useRef, useState, useEffect } from 'react';

import Box from '../../Layout/Box/Box';
import ErrorText from '../../Text/ErrorText/ErrorText';
import styles from './Toggle.module.scss';
import { useDatadogLog } from '../logging';

type OnChangeProps<T> = {
  id?: T;
  isEnabled: boolean;
};

export type ToggleProps<T> = {
  name: T;
  variant?: 'default' | 'icon';
  label?: string;
  value: boolean;
  error?: string | null;
  disabled?: boolean;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, props: OnChangeProps<T>) => void;
};

const Toggle = <T extends string>(props: ToggleProps<T>): React.JSX.Element => {
  const {
    name,
    variant = 'default',
    label,
    value,
    error = null,
    disabled = false,
    onBlur,
    onChange = () => {},
  } = props;
  const [isEnabled, setIsEnabled] = useState(value);
  const wrapperEl = useRef<HTMLInputElement>(null);
  const log = useDatadogLog();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    wrapperEl.current?.blur();

    log({
      message: `${name} was toggled`,
      payload: { name, value },
      actionType: 'Option',
    });

    setIsEnabled(!isEnabled);
    onChange(event, { id: name, isEnabled: !isEnabled });
  };

  const handlerBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur(event);
  };

  useEffect(() => {
    setIsEnabled(value);
  }, [value]);

  return (
    <Box height="auto" gap="none" ai="fe" jc="fe" ps="md" flx="col">
      <Box>
        <p className={[styles['toggle-label'], styles[getLabelClass()]].join(' ')}>{label}</p>
        <div
          data-testid={`toggle-${name}`}
          id={name}
          className={[styles.container, styles[variant]].join(' ')}
          role="button"
          tabIndex={0}
          onBlur={handlerBlur}
          ref={wrapperEl}
        >
          <input
            type="checkbox"
            id={`toggle-${name}`}
            name={name}
            onChange={handleChange}
            checked={isEnabled}
            disabled={disabled}
          />
          <label className={styles[getActiveClass()]} htmlFor={`toggle-${name}`}>
            {/*  */}
          </label>
        </div>
      </Box>
      <div className={[styles.errorContainer, styles[getErrorClass()]].join(' ')}>
        <ErrorText error={error} size="sm" indentation />
      </div>
    </Box>
  );

  function getActiveClass(): string {
    return disabled ? 'disabled' : 'active';
  }

  function getLabelClass(): string {
    return isEnabled ? 'isEnabled' : '';
  }

  function getErrorClass() {
    return error !== '' ? 'hasError' : '';
  }
};

export default Toggle;
